import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface MultisigEntry {
  multisigPda: string;
  createKey: string;
  ownerPubkey: string;
  /**
   * `true` once the backend `multisig.finalize` call has succeeded (i.e. the
   * router knows about this multisig and the signer can vote on its proposals).
   * Until then, the wallet exists on-chain but the 2FA pipeline isn't usable.
   */
  finalized?: boolean;
  /** Tx signature of the on-chain `MultisigCreateV2`. Useful for support / explorer links. */
  createSig?: string;
}

interface MultisigState {
  activePda: string | null;
  list: MultisigEntry[];
  active: () => MultisigEntry | null;
  forOwner: (ownerPubkey: string) => MultisigEntry[];
  pendingFinalize: () => MultisigEntry[];
  add: (entry: MultisigEntry) => void;
  markFinalized: (multisigPda: string) => void;
  select: (multisigPda: string) => void;
  remove: (multisigPda: string) => void;
}

const STORAGE_KEY = 'avocado-solana:multisigs';

export const useMultisigStore = create<MultisigState>()(
  persist(
    (set, get) => ({
      activePda: null,
      list: [],
      active: () => {
        const { list, activePda } = get();
        return list.find((m) => m.multisigPda === activePda) ?? null;
      },
      forOwner: (ownerPubkey: string) =>
        get().list.filter((m) => m.ownerPubkey === ownerPubkey),
      pendingFinalize: () => get().list.filter((m) => !m.finalized),
      add: (entry: MultisigEntry) => {
        const { list } = get();
        const exists = list.some((m) => m.multisigPda === entry.multisigPda);
        const nextList = exists
          ? list.map((m) =>
              m.multisigPda === entry.multisigPda ? { ...m, ...entry } : m,
            )
          : [...list, entry];
        set({ list: nextList, activePda: entry.multisigPda });
      },
      markFinalized: (multisigPda: string) => {
        set({
          list: get().list.map((m) =>
            m.multisigPda === multisigPda ? { ...m, finalized: true } : m,
          ),
        });
      },
      select: (multisigPda: string) => {
        if (get().list.some((m) => m.multisigPda === multisigPda)) {
          set({ activePda: multisigPda });
        }
      },
      remove: (multisigPda: string) => {
        const nextList = get().list.filter((m) => m.multisigPda !== multisigPda);
        const nextActive =
          get().activePda === multisigPda
            ? (nextList[0]?.multisigPda ?? null)
            : get().activePda;
        set({ list: nextList, activePda: nextActive });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ activePda: state.activePda, list: state.list }),
    },
  ),
);
